import React, { useEffect, useRef, useState } from "react"

interface PrioritySelectorProps {
  taskId: string
  currentPriority: number
  onPriorityChange: (taskId: string, priority: number) => void
}

const PrioritySelector: React.FC<PrioritySelectorProps> = ({ taskId, currentPriority, onPriorityChange }) => {
  const [showSelector, setShowSelector] = useState(false)
  const selectorRef = useRef<HTMLDivElement>(null)

  // Close selector when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectorRef.current && !selectorRef.current.contains(event.target as Node)) {
        setShowSelector(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [])

  // Priority options
  const priorityOptions = [
    { value: 0, label: "0 - No Priority", color: " bg-foregroundx " },
    { value: 1, label: "1 - Low", color: " bg-blue-50 hover:bg-blue-100" },
    { value: 2, label: "2 - Medium", color: "text-yellow-600 bg-yellow-50 hover:bg-yellow-100" },
    { value: 3, label: "3 - High", color: "text-orange-600 bg-orange-50 hover:bg-orange-100" },
    { value: 4, label: "4 - Urgent", color: "text-destructive bg-red-50 hover:bg-red-100" },
  ]

  // Helper function to get priority color for current display
  const getPriorityColor = (priority: number) => {
    if (priority >= 4) return "text-destructive bg-red-50"
    if (priority >= 3) return "text-orange-600 bg-orange-50"
    if (priority >= 2) return "text-yellow-600 bg-yellow-50"
    if (priority >= 1) return " bg-blue-50"
    return " bg-foregroundx"
  }

  const handlePrioritySelect = (priority: number) => {
    onPriorityChange(taskId, priority)
    setShowSelector(false)
  }

  return (
    <div className="relative">
      <button
        onClick={(e) => {
          e.stopPropagation()
          setShowSelector(!showSelector)
        }}
        className={`inline-flex items-center px-1.5 py-0.5 text-xs font-medium rounded transition-colors hover:opacity-80 ${getPriorityColor(
          currentPriority,
        )}`}
        title="Change task priority"
      >
        P{currentPriority}
        <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Priority selector popover */}
      {showSelector && (
        <div
          ref={selectorRef}
          className="absolute top-full left-0 mt-1 bg-background border border-gray-300 rounded-lg shadow-lg p-2 z-50 w-48"
        >
          <div className="space-y-1">
            <div className="text-xs font-medium text-primary px-2 py-1">Select Priority:</div>

            {priorityOptions.map((option) => (
              <button
                key={option.value}
                onClick={() => handlePrioritySelect(option.value)}
                className={`w-full text-left px-2 py-1.5 rounded transition-colors ${
                  option.value === currentPriority ? "ring-2 ring-blue-500 " + option.color : option.color
                }`}
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm">{option.label}</span>
                  {option.value === currentPriority && <span className="text-xs">✓</span>}
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default PrioritySelector
